/* eslint-disable */
// @ts-nocheck
import {LinearProgress, Stack, useMediaQuery} from '@mui/material';
import {captureMessage} from '@sentry/browser';

import React, {useEffect, useRef, useState} from 'react';
import graphql from 'babel-plugin-relay/macro';
import {range} from 'lodash';
import {FrameStoreBuffer} from '../types';
import {TruVuFrame} from '../../../lib/truvu/types';
import {
  terminateExistingWorker,
  printIndex,
  sliceIntoChunks,
  logLoadProgress,
  logViewProgress,
  logWorkerPlan,
  deviceCompatibility,
} from '../helpers';
import useWindowDimensions from '../hooks/useWindowDimensions';
import {styled, Theme} from '@mui/material/styles';
import {FullscreenLoader} from '../../../components/loader/FullscreenLoader';
import {useRefetchable} from 'relay-hooks';
import {TourPlayerCanvas_tour$key} from '../../../__generated__/TourPlayerCanvas_tour.graphql';
import {TourErrorDialog} from './TourErrorDialog';
import {TourStrugglingDialog} from './TourStrugglingDialog';
import {tourStrugglingTimeoutDuration} from '../tourStrugglingTimeoutDuration';
import {useSearchParam} from 'react-use';
import {LogoCanvas} from './LogoCanvas';

const fragment = graphql`
  fragment TourPlayerCanvas_tour on Tour
  @refetchable(queryName: "TourPlayerCanvasTodoRefetchQuery") {
    id
    title
    description
    videoURL
    thumbnailURL
    tourIntroLogoURL
    videoInfo {
      width
      height
      fullSize
      mediumSize
      smallSize
    }
    markers {
      id
      name
      description
      scrollPosition
      frameIndex
    }
    clips {
      id
      index
      frameCount
      duration
      highURI
      lowURI
    }
  }
`;

interface TourPlayerCanvasProps {
  getCanvasDimensions: (width: number, height: number) => void;
  tourCanvasRef: React.RefObject<HTMLCanvasElement>;
  tourRef: TourPlayerCanvas_tour$key;
  currentIndex: number;
  hiRes: boolean;
  setBuffer: React.Dispatch<React.SetStateAction<FrameStoreBuffer>>;
}

export function TourPlayerCanvas({
  getCanvasDimensions,
  tourCanvasRef,
  tourRef,
  currentIndex,
  hiRes = false,
  setBuffer,
}: TourPlayerCanvasProps) {
  const {data: tour} = useRefetchable(fragment, tourRef);
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const fullScreenParam = useSearchParam('fullScreen');
  const debugPlayer = useSearchParam('debugPlayer') !== null;
  /** States */
  const [windowDimensions] = useWindowDimensions();
  const [store, setStore] = React.useState<
    Array<{
      frameInClusterId: number;
      frame: TruVuFrame;
      clusterCount: number;
      globalFrameIndex?: number;
      clipIndex?: number;
      localFrameIndex?: number;
      workerIndex?: number;
    }>
  >([]);
  const [lastFrameIndex, setLastFrameIndex] = React.useState(0);
  const [isLibLoaded, setIsLibLoaded] = React.useState(false);
  const [didErrorOccur, setDidErrorOccur] = useState(false);
  const [isStruggling, setIsStruggling] = useState(false);
  const [workerCompletionStatus, setWorkerCompletionStatus] = React.useState<
    Map<number, boolean>
  >(new Map());
  const [frameBuffer, setFrameBuffer] = React.useState<Map<number, any[]>>(
    new Map()
  ); // Buffer frames by clip index
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Helper function to release buffered frames - FIXED: Release available frames without requiring sequential order
  const releaseBufferedFrames = React.useCallback(
    (buffer: Map<number, any[]>, clips: any[]) => {
      const releasableFrames: any[] = [];
      const processedClips: number[] = [];

      // Get all available clips from buffer
      const availableClipIndices = Array.from(buffer.keys()).sort(
        (a, b) => a - b
      );

      // FIXED: Release any clips that are complete, not just sequential ones
      for (const clipIndex of availableClipIndices) {
        const clipFrames = buffer.get(clipIndex);
        if (clipFrames && clipFrames.length > 0) {
          // Find the corresponding clip to get expected frame count
          const clipInfo = clips.find((c) => c.index === clipIndex);
          const expectedFrameCount = clipInfo ? clipInfo.frameCount : 36; // Default to 36 if not found

          // Only release if we have all frames for this clip
          if (clipFrames.length >= expectedFrameCount) {
            releasableFrames.push(...clipFrames);
            processedClips.push(clipIndex);
          }
        }
      }

      // Sort all releasable frames by their global index
      releasableFrames.sort((a, b) => {
        const globalA = a.globalFrameIndex ?? 0;
        const globalB = b.globalFrameIndex ?? 0;
        return globalA - globalB;
      });

      return {releasableFrames, processedClips};
    },
    []
  );

  // Helper function to find closest available frame
  const findClosestFrame = (
    frameMap: Map<number, any>,
    targetIndex: number
  ) => {
    const availableIndices = Array.from(frameMap.keys()).sort((a, b) => a - b);
    if (availableIndices.length === 0) return null;

    let closest = availableIndices[0];
    let minDistance = Math.abs(targetIndex - closest);

    for (const index of availableIndices) {
      const distance = Math.abs(targetIndex - index);
      if (distance < minDistance) {
        minDistance = distance;
        closest = index;
      }
    }

    return frameMap.get(closest);
  };

  /** Memoized states */
  const framesStore = React.useMemo(() => {
    if (store.length === 0) return [];

    // Create a map to handle sparse frame arrays
    const frameMap = new Map();

    // Sort frames by globalFrameIndex (which should always be present from worker)
    const sortedFrames = store.sort((a, b) => {
      const indexA = a.globalFrameIndex ?? 0;
      const indexB = b.globalFrameIndex ?? 0;
      return indexA - indexB;
    });

    // CRITICAL FIX: Use globalFrameIndex directly, don't fall back to sequential index
    sortedFrames.forEach((frameData) => {
      const globalIndex = frameData.globalFrameIndex;

      // Skip frames without proper globalFrameIndex
      if (globalIndex === undefined || globalIndex === null) {
        console.warn('Frame missing globalFrameIndex:', frameData);
        return;
      }

      frameMap.set(globalIndex, {
        frameIndex: globalIndex,
        frame: frameData.frame,
        clusterCount: frameData.clusterCount,
      });
    });

    // Convert map to array, creating a sparse array with correct indices
    const maxIndex = Math.max(...Array.from(frameMap.keys()));
    const sparseArray = [];

    for (let i = 0; i <= maxIndex; i++) {
      if (frameMap.has(i)) {
        sparseArray[i] = frameMap.get(i);
      }
      // Don't fill gaps - leave them undefined so we can detect missing frames
    }

    return sparseArray;
  }, [store]);

  const {totalImages, totalDuration} = React.useMemo(() => {
    return {
      totalImages:
        (tour?.clips?.reduce((accumulator, clip) => {
          return accumulator + clip.frameCount;
        }, 0) ?? 0) - 1,
      totalDuration:
        (tour?.clips?.reduce((accumulator, clip) => {
          return accumulator + clip.duration;
        }, 0) ?? 0) - 1,
    };
  }, [tour?.clips]);

  const progress = React.useMemo(() => (currentIndex / totalImages) * 100, [
    currentIndex,
    totalImages,
  ]);

  const buffer = React.useMemo(() => {
    const actualFrameCount = framesStore.filter(Boolean).length;
    return (actualFrameCount / totalImages) * 100;
  }, [framesStore, totalImages]);

  const {orientation, tourWidth, tourHeight} = React.useMemo(() => {
    let orientation = 'landscape';
    if (tour?.videoInfo?.width && tour?.videoInfo?.width) {
      if (tour?.videoInfo?.width < tour?.videoInfo?.height) {
        orientation = 'portrait';
      }
    }
    return {
      orientation,
      tourWidth: tour?.videoInfo?.width ?? 0,
      tourHeight: tour?.videoInfo?.height ?? 0,
    };
  }, [tour?.videoInfo?.width, tour?.videoInfo?.height]);

  const {canvasHeight, canvasWidth} = React.useMemo(() => {
    let canvasHeight: number;
    let canvasWidth: number;

    if (orientation === 'portrait') {
      canvasHeight = windowDimensions.height;
      canvasWidth = (tourWidth / tourHeight) * windowDimensions.height;
      if (
        windowDimensions.width > canvasWidth &&
        windowDimensions.height > windowDimensions.width
      ) {
        canvasWidth = windowDimensions.width;
        canvasHeight = (tourHeight / tourWidth) * windowDimensions.width;
      }
    } else {
      canvasWidth = windowDimensions.width;
      canvasHeight = (tourHeight / tourWidth) * windowDimensions.width;
    }

    if ((canvasWidth > 640 || canvasHeight > 960) && isDesktop) {
      canvasHeight = tourHeight * 0.5;
      canvasWidth = tourWidth * 0.5;
    }

    if (canvasWidth > 650) {
      canvasWidth = 650;
      canvasHeight = (tourHeight / tourWidth) * 650;
    }

    return {canvasHeight, canvasWidth};
  }, [isDesktop, orientation, tourHeight, tourWidth, windowDimensions]);

  /** Effects */
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setIsStruggling(true);
    }, tourStrugglingTimeoutDuration);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    getCanvasDimensions(canvasWidth, canvasHeight);
  }, [canvasHeight, canvasWidth, getCanvasDimensions]);

  React.useEffect(() => {
    const actualFrameCount = framesStore.filter(Boolean).length;
    setBuffer({
      storeLength: actualFrameCount,
      totalImages,
      fps: totalImages / totalDuration,
    });
    return () => {
      setBuffer({totalImages: 0, storeLength: 0, fps: 0});
    };
  }, [framesStore, setBuffer, totalDuration, totalImages]);

  React.useEffect(() => {
    async function draw() {
      if (tourCanvasRef.current) {
        try {
          if (framesStore.length > 0) {
            // Round to nearest integer and clamp to valid range
            const roundedIndex = Math.round(currentIndex);
            const clampedIndex = Math.max(
              0,
              Math.min(roundedIndex, framesStore.length - 1)
            );

            // Use the frame directly at the clamped index
            const targetFrame = framesStore[clampedIndex];

            if (targetFrame && targetFrame.frame) {
              printIndex(framesStore, clampedIndex, tourCanvasRef.current);
            } else {
              // Find the closest available frame if exact index is missing
              let closestIndex = clampedIndex;
              let searchRadius = 1;
              let foundFrame = null;

              // Search in expanding radius until we find a frame
              while (!foundFrame && searchRadius <= 50) {
                // Check frames before target index
                const beforeIndex = clampedIndex - searchRadius;
                if (beforeIndex >= 0 && framesStore[beforeIndex]) {
                  foundFrame = framesStore[beforeIndex];
                  closestIndex = beforeIndex;
                  break;
                }

                // Check frames after target index
                const afterIndex = clampedIndex + searchRadius;
                if (
                  afterIndex < framesStore.length &&
                  framesStore[afterIndex]
                ) {
                  foundFrame = framesStore[afterIndex];
                  closestIndex = afterIndex;
                  break;
                }

                searchRadius++;
              }

              if (foundFrame) {
                printIndex(framesStore, closestIndex, tourCanvasRef.current);
              } else {
                console.warn(`No frame available near index ${clampedIndex}`);
              }
            }
          }
        } catch (e) {
          console.error('Error drawing frame:', e);
          return;
        }
      }
    }

    requestAnimationFrame(draw);
    if (
      Math.floor(currentIndex) % Math.floor(totalImages / 10) === 0 &&
      Math.floor(currentIndex) !== 0
    ) {
      logViewProgress(
        Math.floor((Math.floor(currentIndex) / totalImages) * 100),
        `${tour?.id}`
      );
    }
  }, [
    currentIndex,
    tourCanvasRef,
    framesStore,
    totalImages,
    tour?.id,
    tour?.tourIntroLogoURL,
    lastFrameIndex,
  ]);

  React.useEffect(() => {
    // TruVu doesn't need external library loading - it's self-contained
    // Just mark as loaded immediately since WASM files are loaded on-demand
    setIsLibLoaded(true);
  }, []);

  // ENHANCED: Debug clip processing order and buffer status (only when debug enabled)
  React.useEffect(() => {
    if (debugPlayer && (store.length > 0 || frameBuffer.size > 0)) {
      const clipIndices = [
        ...new Set(
          store
            .map((frame) => frame.clipIndex)
            .filter((index) => index !== undefined)
        ),
      ];
      clipIndices.sort((a, b) => (a ?? 0) - (b ?? 0));

      const bufferedClips = Array.from(frameBuffer.keys()).sort(
        (a, b) => a - b
      );

      console.log(
        '[TourPlayerCanvas] Store clips:',
        clipIndices,
        '| Buffered clips:',
        bufferedClips
      );

      // Also log worker completion for debugging
      if (workerCompletionStatus.size > 0) {
        console.log(
          '[TourPlayerCanvas] Worker completion status:',
          Array.from(workerCompletionStatus.entries())
            .map(
              ([worker, complete]) =>
                `Worker ${worker}: ${complete ? 'Complete' : 'Processing'}`
            )
            .join(', ')
        );
      }
    }
  }, [debugPlayer, store, frameBuffer, workerCompletionStatus]);

  React.useEffect(() => {
    if (!isLibLoaded) {
      return;
    }

    const workerRefs: Array<React.MutableRefObject<Worker | null>> = [];

    let workerRef: Worker | null = null;

    const fetch = async () => {
      if (!tour?.clips) return;

      // Get device capabilities
      const capabilities = deviceCompatibility(
        tour.clips.length,
        totalDuration
      );

      console.log('[TourPlayerCanvas] Device capabilities:', capabilities);

      const numberOfWorkers = capabilities.numberOfWorkers;
      const shouldUseHiRes = hiRes && capabilities.canHandleHiRes;
      // eslint-disable-next-line
      for (const _ of range(1, numberOfWorkers + 1)) {
        const workerRef = React.createRef<Worker | null>();
        workerRefs.push(workerRef);
      }

      // Clean up any existing blob URLs before creating new ones
      store.forEach((frameData) => {
        if (frameData.frame.blobUrl) {
          URL.revokeObjectURL(frameData.frame.blobUrl);
        }
      });

      setStore([]);
      setFrameBuffer(new Map());
      setLastFrameIndex(0);

      if (tour && tour.videoURL && tour.clips) {
        const clips = tour.clips
          .map((clip) => clip)
          .sort((a, b) => a.index - b.index);

        const hasHiRes =
          shouldUseHiRes && clips.some((clip) => clip.highURI != null);

        // Process clips across multiple workers for parallel processing
        const clipUrls = clips.map((clip) =>
          hasHiRes ? clip.highURI : clip.lowURI
        );
        console.log('[TourPlayerCanvas] Original clipUrls:', clipUrls);
        console.log(
          '[TourPlayerCanvas] Clips with indices:',
          clips.map((c) => ({
            index: c.index,
            url: hasHiRes ? c.highURI : c.lowURI,
          }))
        );

        const urlChunks = sliceIntoChunks(clipUrls, numberOfWorkers);
        console.log('[TourPlayerCanvas] URL chunks after slicing:', urlChunks);

        // CRITICAL FIX: Track which clips each worker will process
        const workerClipAssignments = urlChunks.map((chunk, workerIndex) => {
          return chunk
            .map((url, urlIndexInChunk) => {
              const originalUrlIndex =
                workerIndex * Math.ceil(clipUrls.length / numberOfWorkers) +
                urlIndexInChunk;
              const clip = clips[originalUrlIndex];
              return {
                workerIndex,
                url,
                clipIndex: clip?.index || originalUrlIndex,
                originalUrlIndex,
              };
            })
            .filter((item) => item.url); // Remove any undefined URLs
        });

        console.log(
          '[TourPlayerCanvas] Worker-Clip assignments:',
          workerClipAssignments
        );

        const isPortrait = tour.videoInfo?.width < tour.videoInfo?.height;

        const dimensions = {
          width: hasHiRes ? (isPortrait ? 720 : 1280) : isPortrait ? 360 : 640,
          height: hasHiRes ? (isPortrait ? 1280 : 720) : isPortrait ? 640 : 360,
        };

        // Terminate any existing workers
        await Promise.all(
          workerRefs.map((ref) => terminateExistingWorker(ref.current, store))
        );

        // Create and start multiple TruVu workers
        const workers: Worker[] = [];

        for (let i = 0; i < numberOfWorkers; i++) {
          if (urlChunks[i] && urlChunks[i].length > 0) {
            const worker = new Worker('truvu-worker.js');
            workers.push(worker);
            workerRefs[i].current = worker;

            worker.onerror = (error) => {
              console.error(`[TruVu Worker ${i}] ERROR:`, error);
              captureMessage(error.message);
            };

            worker.onmessage = (data) => {
              if (data.data && data.data.frames) {
                /** Clear and reset struggling timeout related state */
                setIsStruggling(false);
                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current);
                }

                // BUFFERED APPROACH: Store frames by clip index first
                const newFrames = data.data.frames.map((frameData: any) => ({
                  frameInClusterId: frameData.frameInClusterId,
                  frame: frameData.frame,
                  clusterCount: frameData.clusterCount,
                  globalFrameIndex: frameData.globalFrameIndex,
                  clipIndex: frameData.clipIndex,
                  localFrameIndex: frameData.localFrameIndex,
                  workerIndex: i,
                }));

                // Group new frames by clip index
                const framesByClip = new Map<number, any[]>();
                newFrames.forEach((frame) => {
                  const clipIndex = frame.clipIndex ?? 0;
                  if (!framesByClip.has(clipIndex)) {
                    framesByClip.set(clipIndex, []);
                  }
                  framesByClip.get(clipIndex)!.push(frame);
                });

                // Update frame buffer
                setFrameBuffer((prevBuffer) => {
                  const newBuffer = new Map(prevBuffer);

                  // Add new frames to buffer by clip
                  framesByClip.forEach((frames, clipIndex) => {
                    const existingFrames = newBuffer.get(clipIndex) || [];
                    newBuffer.set(clipIndex, [...existingFrames, ...frames]);
                  });

                  if (debugPlayer) {
                    console.log(
                      '[TourPlayerCanvas] Frame buffer updated:',
                      Array.from(newBuffer.entries())
                        .map(
                          ([clip, frames]) =>
                            `Clip ${clip}: ${frames.length} frames`
                        )
                        .join(', ')
                    );
                  }

                  // Try to release frames in order
                  const {
                    releasableFrames,
                    processedClips,
                  } = releaseBufferedFrames(newBuffer, clips);

                  if (releasableFrames.length > 0) {
                    if (debugPlayer) {
                      console.log(
                        '[TourPlayerCanvas] Releasing frames from clips:',
                        processedClips.sort((a, b) => a - b)
                      );
                    }

                    // Release frames to main store - ADD to existing store instead of replacing
                    setStore((prevStore) => {
                      // Combine previous store with new releasable frames
                      const combinedFrames = [
                        ...prevStore,
                        ...releasableFrames,
                      ];

                      // Remove duplicates based on globalFrameIndex
                      const uniqueFrames = combinedFrames.filter(
                        (frame, index, arr) => {
                          return (
                            arr.findIndex(
                              (f) =>
                                f.globalFrameIndex === frame.globalFrameIndex
                            ) === index
                          );
                        }
                      );

                      // Sort by global frame index to maintain order
                      return uniqueFrames.sort((a, b) => {
                        const globalA = a.globalFrameIndex ?? 0;
                        const globalB = b.globalFrameIndex ?? 0;
                        return globalA - globalB;
                      });
                    });

                    // Remove released clips from buffer
                    const updatedBuffer = new Map(newBuffer);
                    processedClips.forEach((clipIndex) => {
                      updatedBuffer.delete(clipIndex);
                    });

                    logLoadProgress(
                      releasableFrames.length,
                      totalImages,
                      `${tour?.id}`
                    );

                    return updatedBuffer;
                  }

                  return newBuffer;
                });

                // Track worker completion
                setWorkerCompletionStatus((prev) => {
                  const newStatus = new Map(prev);
                  newStatus.set(i, true);
                  return newStatus;
                });

                setDidErrorOccur(false);
              } else if (data.data && data.data.type === 'error') {
                setDidErrorOccur(true);
                captureMessage(data.data.message);
              } else if (data.data && data.data.type === 'progress') {
                console.log(
                  `[TruVu Worker ${i}] Progress: ${data.data.current}/${data.data.total}`
                );
              } else if (data.data && data.data.type === 'complete') {
                console.log(
                  `[TruVu Worker ${i}] Complete: ${data.data.totalFrames} total frames`
                );

                // ENHANCED: Mark this worker as fully complete
                setWorkerCompletionStatus((prev) => {
                  const newStatus = new Map(prev);
                  newStatus.set(i, true);
                  return newStatus;
                });
              }
            };

            // FIXED: Calculate frame offsets correctly for each clip in this worker's chunk
            // Get the clip numbers for this worker's URLs
            const thisWorkerClipNumbers = urlChunks[i].map((url) => {
              // Handle different URL formats: h4.webm, l11.webm, _m0.webm, _m1.webm
              const match = url.match(/[_]?[hlm](\d+)\.webm/);
              return match ? parseInt(match[1], 10) : 0;
            });

            // Calculate individual frame offsets for each clip this worker will process
            const clipFrameOffsets = thisWorkerClipNumbers.map((clipIndex) => {
              let frameOffset = 0;
              // Sum up frame counts for all clips that come before this clip
              for (let j = 0; j < clips.length; j++) {
                if (clips[j].index < clipIndex) {
                  frameOffset += clips[j].frameCount;
                }
              }
              return {clipIndex, frameOffset};
            });

            if (debugPlayer) {
              console.log(
                `[TruVu Worker ${i}] Clip numbers for this worker:`,
                thisWorkerClipNumbers
              );
              console.log(
                `[TruVu Worker ${i}] Calculated frame offsets:`,
                clipFrameOffsets
              );
            }

            // Send this worker's chunk of URLs with individual frame offsets
            console.log(`[TruVu Worker ${i}] Sending config:`, {
              urls: urlChunks[i],
              urlsType: typeof urlChunks[i],
              isArray: Array.isArray(urlChunks[i]),
              length: urlChunks[i]?.length,
              clipFrameOffsets: clipFrameOffsets, // Send individual offsets instead of single frameOffset
            });

            worker.postMessage({
              type: 'INIT',
              config: {
                urls: urlChunks[i], // This worker's chunk of URLs
                width: dimensions.width,
                height: dimensions.height,
                wasmBasePath: '/',
                debugEnabled: debugPlayer,
                clipFrameOffsets: clipFrameOffsets, // Send individual offsets for each clip
                allClips: clips, // Send all clip information
              },
            });
          }
        }

        logWorkerPlan(numberOfWorkers, true, `${tour?.id}`); // Multiple workers with TruVu
      }
    };

    fetch();

    return () => {
      // Clean up blob URLs to prevent memory leaks
      store.forEach((frameData) => {
        if (frameData.frame.blobUrl) {
          URL.revokeObjectURL(frameData.frame.blobUrl);
        }
      });

      setStore([]);
      setFrameBuffer(new Map());
      setLastFrameIndex(0);
      // Terminate all workers
      workerRefs.forEach((ref) => {
        if (ref.current) {
          terminateExistingWorker(ref.current, store);
        }
      });
    };
    // eslint-disable-next-line
  }, [isLibLoaded]);

  if (
    framesStore.length === 0 ||
    (framesStore.length > 0 && framesStore.filter(Boolean).length === 0) // Check if we have any actual frames
  ) {
    return (
      <>
        <TourErrorDialog
          isOpen={didErrorOccur}
          onClose={() => {
            setDidErrorOccur(false);
          }}
        />
        <TourStrugglingDialog
          isOpen={isStruggling}
          onClose={() => {
            setIsStruggling(false);
          }}
        />
        <FullscreenLoader />
      </>
    );
  }

  return (
    <Stack
      position="relative"
      maxHeight={windowDimensions.height}
      maxWidth={windowDimensions.width}
      overflow="hidden"
      justifyContent="center"
      alignItems="center"
      borderRadius={
        canvasWidth >= windowDimensions.width &&
        canvasHeight >= windowDimensions.height
          ? 0
          : '8px'
      }
      style={{objectPosition: 'center'}}
    >
      {tour?.tourIntroLogoURL && (
        <LogoCanvas logoUrl={tour.tourIntroLogoURL} percentage={progress} />
      )}
      <TourErrorDialog
        isOpen={didErrorOccur}
        onClose={() => {
          setDidErrorOccur(false);
        }}
      />
      <canvas
        style={{
          width: fullScreenParam === 'true' ? '100vw' : canvasWidth,
          height: fullScreenParam === 'true' ? '100svh' : canvasHeight,
          objectFit: 'cover',
          objectPosition: 'center',
        }}
        ref={tourCanvasRef}
      />
      <BorderLinearProgress
        variant="buffer"
        value={progress > 0 ? progress + 1 : progress}
        valueBuffer={buffer}
      />
    </Stack>
  );
}

const BorderLinearProgress = styled(LinearProgress)(() => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: 5,
  borderRadius: 5,
}));
